import { useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ImageSquare, DownloadSimple, Lightning, Trophy, Moon, Fire } from "@phosphor-icons/react";

const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
const recColor = (v) => (v == null ? "#8899a6" : v >= 67 ? "#00e676" : v >= 34 ? "#ffea00" : "#ff3b30");
const avg = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);

function monthStats(days, workouts, ym) {
  const md = days.filter((d) => d.date.startsWith(ym));
  const recs = md.filter((d) => d.recovery != null);
  if (recs.length < 5) return null;
  const best = recs.reduce((a, b) => (b.recovery > a.recovery ? b : a));
  const sleeps = md.map((d) => d.sleep_h).filter((v) => v != null);
  const strains = md.map((d) => d.strain).filter((v) => v != null);
  const hrvs = md.map((d) => d.hrv).filter((v) => v != null);
  return {
    ym,
    avgRec: Math.round(avg(recs.map((d) => d.recovery))),
    greenPct: Math.round((recs.filter((d) => d.recovery >= 67).length / recs.length) * 100),
    avgSleep: sleeps.length ? Math.round(avg(sleeps) * 10) / 10 : null,
    avgStrain: strains.length ? Math.round(avg(strains) * 10) / 10 : null,
    maxHrv: hrvs.length ? Math.max(...hrvs) : null,
    best,
    workoutsN: workouts.filter((w) => (w.date || "").startsWith(ym)).length,
    daysN: recs.length,
  };
}

export default function MonthReportCard({ days, workouts }) {
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);
  const months = useMemo(() => {
    const set = [...new Set(days.filter((d) => d.recovery != null).map((d) => d.date.slice(0, 7)))].sort().reverse();
    return set.slice(0, 12);
  }, [days]);
  const [ym, setYm] = useState(null);
  const activeYm = ym || months[0];
  const s = useMemo(() => (activeYm ? monthStats(days, workouts || [], activeYm) : null), [days, workouts, activeYm]);
  if (!months.length) return null;

  const download = () => {
    toPng(cardRef.current, { pixelRatio: 2 }).then((url) => {
      const a = document.createElement("a");
      a.href = url;
      a.download = `lifeos-${activeYm}.png`;
      a.click();
      toast.success("Открытка сохранена");
    }).catch(() => toast.error("Не удалось сохранить изображение"));
  };

  const title = (m) => `${MONTHS[parseInt(m.slice(5, 7), 10) - 1]} ${m.slice(0, 4)}`;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="month-report-open-button">
        <ImageSquare size={15} className="mr-1.5" /> Открытка месяца
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="month-report-dialog">
          <DialogTitle className="text-base">Итоги месяца</DialogTitle>
          <div className="flex gap-1.5 flex-wrap mb-2">
            {months.slice(0, 6).map((m) => (
              <button key={m} onClick={() => setYm(m)} data-testid={`month-select-${m}`}
                className={`px-2.5 py-1 rounded text-xs font-semibold transition-colors ${activeYm === m ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {title(m)}
              </button>
            ))}
          </div>
          {s ? (
            <>
              <div ref={cardRef} className="rounded-xl p-6 border border-border/60" data-testid="month-report-card"
                style={{ background: "linear-gradient(145deg, hsl(220 15% 9%), hsl(225 20% 13%))" }}>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">LifeOS · Итоги месяца</div>
                    <div className="font-heading text-2xl font-bold">{title(s.ym)}</div>
                  </div>
                  <div className="text-center">
                    <div className="font-heading text-4xl font-bold num" style={{ color: recColor(s.avgRec) }}>{s.avgRec}%</div>
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">ср. восстановление</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    [<Fire size={14} weight="fill" className="text-[#00e676]" />, "Зелёных дней", `${s.greenPct}%`],
                    [<Moon size={14} weight="fill" className="text-[#a970ff]" />, "Средний сон", s.avgSleep != null ? `${s.avgSleep} ч` : "—"],
                    [<Lightning size={14} weight="fill" className="text-[#0084ff]" />, "Ср. нагрузка", s.avgStrain ?? "—"],
                    [<Trophy size={14} weight="fill" className="text-[#ffea00]" />, "Тренировок", s.workoutsN],
                  ].map(([icon, l, v]) => (
                    <div key={l} className="rounded-lg bg-white/[0.04] border border-white/5 p-3">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{icon}{l}</div>
                      <div className="num font-bold text-xl">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-lg bg-white/[0.04] border border-white/5 p-3 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Лучший день · {s.best.date.slice(8, 10)}.{s.best.date.slice(5, 7)}</span>
                  <span className="num font-bold" style={{ color: recColor(s.best.recovery) }}>{Math.round(s.best.recovery)}%{s.maxHrv != null && <span className="text-muted-foreground font-normal text-xs"> · HRV до {Math.round(s.maxHrv)} мс</span>}</span>
                </div>
                <div className="mt-4 text-[9px] text-muted-foreground text-center tracking-widest uppercase">{s.daysN} дней с данными Whoop · lifeos</div>
              </div>
              <Button onClick={download} className="w-full" data-testid="month-report-download-button">
                <DownloadSimple size={16} className="mr-1.5" /> Скачать PNG
              </Button>
            </>
          ) : <p className="text-sm text-muted-foreground p-4">Недостаточно данных за этот месяц.</p>}
        </DialogContent>
      </Dialog>
    </>
  );
}
